/**
 * @typedef {Object} InvoiceCustomerSearchRow
 * @property {string} senderName
 * @property {string} [senderEmail]
 * @property {string} [senderPhone]
 * @property {number} [parcelCount]
 * @property {string} [bookedById]
 */

/**
 * @typedef {Object} InvoiceParcelRow
 * @property {number} parcelId
 * @property {string} [recordSerialNo]
 * @property {string} [barcode]
 * @property {number} [orderPayableAmount]
 * @property {string} [creationDate]
 * @property {string} [bookedById]
 * @property {number} [activeInvoiceCount]
 * @property {string} [senderName]
 * @property {string} [senderEmail]
 * @property {string} [senderPhone]
 * @property {string} [senderPostCode]
 */

/**
 * @typedef {"None"|"Amended"|"Due"|"Credit"|"Superseded"|string} InvoiceAdjustmentType
 */

/**
 * @typedef {Object} InvoiceVersionRow
 * @property {number} [id]
 * @property {number} [paymentId]
 * @property {number} version
 * @property {string} paymentInvoiceNo
 * @property {number} orderAmount
 * @property {number} [paymentStatusId]
 * @property {string} [statusName]
 * @property {InvoiceAdjustmentType} [adjustmentType]
 * @property {number|null} [parentPaymentId]
 * @property {string} [creationDate]
 * @property {string} [description]
 * @property {string} [reason]
 * @property {boolean} [isCurrent]
 * @property {string} [currencyType]
 * @property {string} [currencyCode]
 * @property {string} [symbol]
 * @property {string} [currencySymbol]
 * @property {number} [currencyTypeId]
 */

/**
 * Historical (or live-fallback) product line from GetInvoiceForPrint.
 * @typedef {Object} InvoicePrintLine
 * @property {number} [id]
 * @property {number} [paymentId]
 * @property {number} [parcelId]
 * @property {number} [sourceParcelDetailId]
 * @property {number} [routeId]
 * @property {number} [routeCost]
 * @property {number} [shippingServiceId]
 * @property {number} [shippingServiceAmount]
 * @property {number} [discountPercentage]
 * @property {number} [discountAmount]
 * @property {boolean} [isExtraPackaging]
 * @property {number} [extraPackagingCost]
 * @property {boolean} [isPickup]
 * @property {number} [pickUpCost]
 * @property {number} [itemTypeId]
 * @property {number} [itemTypeCost]
 * @property {number} [weightId]
 * @property {number} [weightCost]
 * @property {string} [weightDescription]
 * @property {number} [minimumWeight]
 * @property {number} [maximumWeight]
 * @property {number} [dimensionId]
 * @property {number} [dimensionCost]
 * @property {number} [cargoTypeId]
 * @property {number} [cargoCost]
 * @property {string} [parcelAdditionalInfo]
 * @property {string} [uniqItemDescription]
 * @property {number} [itemValue]
 * @property {number} [paymentTypeMethodId]
 * @property {number} [productQty]
 * @property {number} [itemCategoryId]
 * @property {number} [itemCategoryCost]
 * @property {number} [parcelLength]
 * @property {number} [parcelWidth]
 * @property {number} [parcelHeight]
 * @property {number} [parcelWeight]
 * @property {number} [customerParcelLength]
 * @property {number} [customerParcelWidth]
 * @property {number} [customerParcelHeight]
 * @property {number} [customerParcelWeight]
 */

/**
 * GET InvoiceManager/GetInvoiceForPrint/{invoiceNo}
 * @typedef {Object} InvoiceForPrint
 * @property {number} [paymentId]
 * @property {string} [paymentInvoiceNo]
 * @property {number} [parcelId]
 * @property {string} [recordSerialNo]
 * @property {string} [barcode]
 * @property {number} orderAmount
 * @property {number} [paymentStatusId]
 * @property {string} [statusName]
 * @property {number} [paymentMethodId]
 * @property {string} [paymentMethodName]
 * @property {InvoiceAdjustmentType} [adjustmentType]
 * @property {number|null} [parentPaymentId]
 * @property {number} [currencyTypeId]
 * @property {string} [currencyType]
 * @property {string} [currencySymbol]
 * @property {string} [description]
 * @property {string} [creationDate]
 * @property {string} [paymentDate]
 * @property {number} [currentParcelSubTotal]
 * @property {number} [currentParcelVatAmount]
 * @property {number} [currentParcelPayableAmount]
 * @property {string} [senderName]
 * @property {string} [senderEmail]
 * @property {string} [senderPhone]
 * @property {string} [senderDoorNo]
 * @property {string} [senderStreetName]
 * @property {string} [senderPostCode]
 * @property {string} [receiverName]
 * @property {string} [receiverEmail]
 * @property {string} [receiverPhone]
 * @property {string} [receiverDoorNo]
 * @property {string} [receiverStreetName]
 * @property {string} [receiverPostCode]
 * @property {boolean} [hasSnapshot]
 * @property {boolean} [usedLiveFallback]
 * @property {InvoicePrintLine[]} [lines]
 */

/**
 * One row from GetPaymentHistoryByParcelId items[].
 * @typedef {Object} ParcelPaymentHistoryItem
 * @property {number} [sequence]
 * @property {number} [paymentId]
 * @property {string} [paymentInvoiceNo]
 * @property {number} [orderAmount]
 * @property {number} [paymentStatusId]
 * @property {string} [statusName]
 * @property {number} [paymentMethodId]
 * @property {string} [paymentMethodName]
 * @property {string|null} [paymentDate]
 * @property {string} [creationDate]
 * @property {string} [eventLabel]
 * @property {boolean} [isPaid]
 * @property {boolean} [isPending]
 * @property {InvoiceAdjustmentType|null} [adjustmentType]
 * @property {string} [description]
 * @property {string} [reason]
 * @property {string} [currencySymbol]
 * @property {string} [symbol]
 */

/**
 * GET InvoiceManager/GetPaymentHistoryByParcelId/{parcelId}
 * @typedef {Object} ParcelPaymentHistory
 * @property {number} [parcelId]
 * @property {string} [paymentStatus] Pending | Paid | Credit | N/A
 * @property {number} [totalPayments]
 * @property {number} [paidCount]
 * @property {number} [totalPaidAmount]
 * @property {number} [totalPendingAmount]
 * @property {ParcelPaymentHistoryItem[]} [items]
 */

export const INVOICE_ADJUSTMENT = {
  NONE: "None",
  AMENDED: "Amended",
  DUE: "Due",
  CREDIT: "Credit",
  SUPERSEDED: "Superseded",
};

export const LIVE_FALLBACK_NOTE =
  "Historical product details unavailable for this invoice; showing current booking details.";
